/*
 * TanStack Query Client
 *
 * Features:
 * - Provides the singleton in-memory server-state cache for the React application
 * - Defines explicit freshness, garbage-collection, refetch, and retry behavior
 *
 * Implementation:
 * - Leaves authentication refresh and HTTP response handling in dataService
 * - Suppresses Query-level retries for every authorization error shape emitted by dataService
 * - Executes mutations immediately so offline writes fail instead of queuing for reconnect
 *
 * Changes:
 * - 2026-08-06: Added the shared Query client for the account/contact cache pilot
 */

import { QueryClient, type QueryClientConfig } from '@tanstack/react-query';

export const SERVER_DATA_STALE_TIME_MS = 60_000;
export const SERVER_DATA_GC_TIME_MS = 600_000;

const AUTHORIZATION_MESSAGE_PATTERNS = [
  /unauthorized/i,
  /user not authorized/i,
  /status(?:\s*:)?\s*(?:401|403)\b/i,
];

export function isAuthorizationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  const status = (error as { status?: unknown }).status;
  if (status === 401 || status === 403) return true;

  const message = (error as { message?: unknown }).message;
  return typeof message === 'string'
    && AUTHORIZATION_MESSAGE_PATTERNS.some(pattern => pattern.test(message));
}

export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  return failureCount < 1 && !isAuthorizationError(error);
}

export const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: SERVER_DATA_STALE_TIME_MS,
      gcTime: SERVER_DATA_GC_TIME_MS,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery,
    },
    mutations: {
      retry: false,
      networkMode: 'always',
    },
  },
};

export const createQueryClient = () => new QueryClient(queryClientConfig);

export const queryClient = createQueryClient();
